import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, List, ListItem, ListItemText, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import { useRef } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { selectCanAccess, selectCanDo } from "../app/auth";
import { layersApi } from "../app/layers";
import { orthophotosApi } from "../app/orthophotos";

// Ivon-e utils
import { cleanCorruptedAnnotation, findParentKey } from "../app/ivone_utils.js";
import { buildAnnotationsFromGeojson } from "../app/importParsers/buildAnnotationsFromGeojson.js";
import { importAnnotationsToDb } from "../app/importParsers/importAnnotationsToDb.js";

import ImportConverter from "./ImportConverter.jsx";

// api client
import {createAnnotationOnAPI} from "../app/annotations.js";
import { runInParallel } from "../app/runInParallel.js";
import apiClient from "../app/apiClient.js";


const ProjectImportHandler = ({project, setSnackbar}) => {
  const urlParams = useParams();
  const projectId = urlParams.id;
  const canDo = useSelector(selectCanDo);
  const navigate = useNavigate();

  // Inspection and view chosen by the user:
  const [view, setView] = useState(null);
  const [inspectionId, setInspectionId] = useState(null);

  // List of errors
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);

  // View fields matching: // Key: field name, value: {"field_title": "...", "matched_json_field": "..."}
  const [fieldMatching, setFieldMatching] = useState({});
  const [jsonFields, setJsonFields] = useState([]);

  // Annotations
  const [geoJSON, setGeoJSON] = useState(null);
  const [dxfRawGeoJSON, setDxfRawGeoJSON] = useState(null);
  const [dxfMappingApplied, setDxfMappingApplied] = useState(false);
  const [annotationsToCreate, setAnnotationsToCreate] = useState([]);
  const [isDXFFile, setIsDXFFile] = useState(false);

  // Is importing
  const [isImporting, setIsImporting] = useState(false);
  const parsingStartedAtRef = useRef(0);
  
  // DXF Layer mapping: manual mapping of layers to keys/options
  // Rule:
  // - if key.id exists => use it
  // - else => use key.name (NOT _id)
  // Structure: { layerName: { keyId: string, optionId: string, parentKeyId?: string, parentOptionId?: string, comment?: string, ignored?: boolean } }
  const [layerMapping, setLayerMapping] = useState({});
  const [uniqueLayers, setUniqueLayers] = useState([]);
  // DXF mapping is a 2-step process:
  // - "ready": all layers have key+option selected
  // - "validated": user clicked Validate mapping (only then we proceed to the standard GeoJSON import flow)
  const [layerMappingReady, setLayerMappingReady] = useState(false);
  const [layerMappingValidated, setLayerMappingValidated] = useState(false);
  
  // When the user select the inspection where to import annotations
  const handleInspectionChange = (e) => {
    // Find corresponding inspection and save in state
    const { name, value } = e.target;
    let inspectionId = null;
    for (const mission of project.missions){
      for (const inspection of mission.inspections){
        for (const view of inspection.views){
          if (view.id === value){
            setView(view);
            setInspectionId(inspection.id);
            console.log('[ProjectImportHandler] View loaded:', view);
          }
        }
      }
    }    
  };

  // When the user changes the matching
  const handleMatchingChange = (e, key_name) => {
    // Find corresponding inspection and save in state
    const { name, value } = e.target;
    let matching_copy = fieldMatching;
    matching_copy[key_name].matched_json_field = value;
    setFieldMatching({...matching_copy});
  };

  const applyImportedGeojson = (out) => {
    if (!out?.geojson || !Array.isArray(out.geojson.features)) return;

    const geojson = out.geojson;
    setGeoJSON(geojson);
    setAnnotationsToCreate([]);
    setWarnings([]);
    setErrors([]);

    const isDxf = out.format === "dxf";
    setIsDXFFile(isDxf);
    setDxfMappingApplied(false);
    setDxfRawGeoJSON(isDxf ? (out.dxfRawGeoJSON || geojson) : null);
    setUniqueLayers(isDxf ? (out.uniqueLayers || []) : []);
    setLayerMapping(isDxf ? (out.layerMapping || {}) : {});
    setLayerMappingReady(false);
    setLayerMappingValidated(false);

    // List fields + default matching (GeoJSON & CSV)
    const categories = Array.from(
      new Set(
        (geojson.features || []).flatMap((f) => Object.keys(f?.properties || {}))
      )
    );
    setJsonFields(categories);

    const matching_init = {};
    for (const key of view.spec.description.keys) {
      const foundField =
        categories.find((json_field_name) => json_field_name === key.name) ??
        categories.find((json_field_name) => json_field_name === key.title) ??
        categories.find((json_field_name) => key.title?.startsWith(json_field_name));
      matching_init[key.name] = {
        field_title: key.title,
        matched_json_field: foundField ?? "__default__",
      };
    }
    setFieldMatching(matching_init);
  };

  // Clear the imported GeoJSON states (for both GeoJSON and DXF files)
  // set to empty every states used to import a file
  const clearImportedGeojson = () => {
    setGeoJSON(null);
    setJsonFields([]);
    setFieldMatching({});
    setWarnings([]);
    setErrors([]);
    setAnnotationsToCreate([]);
    setIsDXFFile(false);
    setDxfRawGeoJSON(null);
    setDxfMappingApplied(false);
    setUniqueLayers([]);
    setLayerMapping({});
    setLayerMappingReady(false);
    setLayerMappingValidated(false);
  };

  // Handle layer mapping changes for DXF files
  const findKeyByIdOrName = (keyIdOrName, allKeys) => {
    if (!keyIdOrName) return null;
    const byId = allKeys.find(k => k.id === keyIdOrName);
    if (byId) return byId;
    return allKeys.find(k => k.name === keyIdOrName) ?? null;
  };

  // Options may have only .text (no .id); use a single value for Select and lookups
  const getOptionValue = (opt) => opt.id ?? opt.text ?? '';
  const optionMatchesValue = (opt, value) =>
    (opt.id != null && String(opt.id) === String(value)) ||
    (opt.text != null && String(opt.text) === String(value));

  const getParentKeysForSubmenu = (subKeyId, allKeys) => {
    if (!subKeyId) return [];
    const parentKeys = [];
    for (const k of allKeys) {
      if (k.type !== "select") continue;
      for (const opt of k.options || []) {
        if ((opt.submenus || []).includes(subKeyId)) {
          parentKeys.push(k);
          break;
        }
      }
    }
    return parentKeys;
  };

  const handleLayerKeyChange = (layerName, keyId) => {
    setLayerMapping(prev => {
      const newMapping = { ...prev };
      if (!newMapping[layerName]) {
        newMapping[layerName] = { keyId: '', optionId: '', comment: '' };
      }
      newMapping[layerName] = {
        ...newMapping[layerName],
        keyId: keyId,
        optionId: '', // Reset option when key changes
        parentKeyId: undefined,
        parentOptionId: undefined
        // Keep existing comment when key changes
      };
      
      // If the selected key is a submenu, we may have multiple possible parent keys/options.
      if (keyId && view) {
        const selectedKey = findKeyByIdOrName(keyId, view.spec.description.keys);
        if (selectedKey) {
          const parentKeys = getParentKeysForSubmenu(selectedKey.id, view.spec.description.keys);
          if (parentKeys.length > 0) {
            // Default: first parent key, and first parent option that contains this submenu
            const defaultParentKey = parentKeys[0];
            const parentOptions = (defaultParentKey.options || []).filter(opt => (opt.submenus || []).includes(selectedKey.id));
            if (parentOptions.length > 0) {
              newMapping[layerName].parentKeyId = defaultParentKey.id ?? defaultParentKey.name;
              newMapping[layerName].parentOptionId = getOptionValue(parentOptions[0]);
            }
          }
        }
      }
      
      return newMapping;
    });
  };

  const handleLayerOptionChange = (layerName, optionId) => {
    setLayerMapping(prev => {
      const newMapping = { ...prev };
      if (!newMapping[layerName]) {
        newMapping[layerName] = { keyId: '', optionId: '', comment: '' };
      }
      newMapping[layerName] = {
        ...newMapping[layerName],
        optionId: optionId
        // Keep existing comment when option changes
      };
      return newMapping;
    });
  };

  const handleLayerParentKeyChange = (layerName, parentKeyId) => {
    setLayerMapping(prev => {
      const newMapping = { ...prev };
      if (!newMapping[layerName]) {
        newMapping[layerName] = { keyId: '', optionId: '', comment: '' };
      }
      newMapping[layerName] = {
        ...newMapping[layerName],
        parentKeyId: parentKeyId,
        parentOptionId: '' // Reset parent option when parent key changes
        // Keep existing comment when parent key changes
      };
      return newMapping;
    });
  };

  const handleLayerParentOptionChange = (layerName, parentOptionId) => {
    setLayerMapping(prev => {
      const newMapping = { ...prev };
      if (!newMapping[layerName]) {
        newMapping[layerName] = { keyId: '', optionId: '', comment: '' };
      }
      newMapping[layerName] = {
        ...newMapping[layerName],
        parentOptionId: parentOptionId
        // Keep existing comment when parent option changes
      };
      return newMapping;
    });
  };

  // Find comment/remarque field in view (textarea type)
  const commentField = view?.spec.description.keys?.find(key => {
    const nameLower = (key.name || '').toLowerCase();
    const titleLower = (key.title || '').toLowerCase();
    return key.type === 'textarea' && (
      nameLower.includes('commentaire') || nameLower.includes('remarque') ||
      titleLower.includes('commentaire') || titleLower.includes('remarque')
    );
  });

  const handleLayerCommentChange = (layerName, comment) => {
    setLayerMapping(prev => {
      const newMapping = { ...prev };
      if (!newMapping[layerName]) {
        newMapping[layerName] = { keyId: '', optionId: '', comment: '', ignored: false };
      }
      newMapping[layerName] = {
        ...newMapping[layerName],
        comment: comment || ''
      };
      return newMapping;
    });
  };

  const handleLayerIgnoredChange = (layerName, ignored) => {
    setLayerMapping(prev => {
      const newMapping = { ...prev };
      if (!newMapping[layerName]) {
        newMapping[layerName] = { keyId: '', optionId: '', comment: '', ignored: false };
      }
      newMapping[layerName] = {
        ...newMapping[layerName],
        ignored: Boolean(ignored),
      };
      return newMapping;
    });
  };

  const downloadCurrentGeoJSON = () => {
    if (!geoJSON) return;
    try {
      const blob = new Blob([JSON.stringify(geoJSON, null, 2)], { type: "application/geo+json;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = isDXFFile ? "dxf_converted.geojson" : "import.geojson";
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      setSnackbar({
        open: true,
        message: `Error while exporting GeoJSON: ${e.message}`,
        severity: "error"
      });
    }
  };

  const validateLayerMapping = () => {
    if (!layerMappingReady) return;
    setLayerMappingValidated(true);
  };

  // Check if layer mapping is complete
  useEffect(() => {
    if (isDXFFile && uniqueLayers.length > 0) {
      const allMapped = uniqueLayers.every(layer => {
        const mapping = layerMapping[layer];
        if (mapping?.ignored) return true;
        if (!mapping || !mapping.keyId || !mapping.optionId) return false;
        if (!view) return true;
        const selectedKey = findKeyByIdOrName(mapping.keyId, view.spec.description.keys);
        if (!selectedKey) return false;
        // Only treat as submenu when the key has an id and is referenced by parent options
        const parentKeys = selectedKey.id ? getParentKeysForSubmenu(selectedKey.id, view.spec.description.keys) : [];
        const isSubmenu = parentKeys.length > 0;
        if (!isSubmenu) return true;
        return Boolean(mapping.parentKeyId && mapping.parentOptionId);
      });
      setLayerMappingReady(allMapped);
    } else if (!isDXFFile) {
      setLayerMappingReady(false);
      setLayerMappingValidated(false);
    }
  }, [layerMapping, uniqueLayers, isDXFFile, view]);

  // Transform DXF GeoJSON after layer mapping is complete to create fieldMatching
  // This allows DXF to use the same checking flow as GeoJSON
  useEffect(() => {
    if (!isDXFFile) return;
    if (!layerMappingValidated) return;
    if (!view) return;
    if (!dxfRawGeoJSON) return;
    if (dxfMappingApplied) return;

      // Transform GeoJSON features to add mapped properties
      const transformedGeoJSON = {
        ...dxfRawGeoJSON,
        features: dxfRawGeoJSON.features.map(feature => {
          const layerName = feature.properties?.layer;
          const newProperties = { ...feature.properties };
          
          if (layerName && layerMapping[layerName]) {
            const mapping = layerMapping[layerName];
            if (mapping.ignored) {
              return { ...feature, properties: newProperties };
            }
            if (mapping.keyId && mapping.optionId) {
              // Get the option text from the optionId
              const selectedKey = findKeyByIdOrName(mapping.keyId, view.spec.description.keys);
              const selectedOption = selectedKey?.options?.find(opt => optionMatchesValue(opt, mapping.optionId));
              
              if (selectedOption) {
                // Add the mapped field with option text (like GeoJSON import)
                newProperties[selectedKey.name] = selectedOption.text;
                
                // If it's a submenu, add parent field
                if (mapping.parentKeyId && mapping.parentOptionId) {
                  const parentKey = findKeyByIdOrName(mapping.parentKeyId, view.spec.description.keys);
                  const parentOption = parentKey?.options?.find(opt => optionMatchesValue(opt, mapping.parentOptionId));
                  if (parentOption) {
                    newProperties[parentKey.name] = parentOption.text;
                  }
                }
              }
            }
            
            // Add comment if commentField exists and comment is provided
            if (commentField && mapping.comment) {
              newProperties[commentField.name] = mapping.comment;
            }
          }
          
          return {
            ...feature,
            properties: newProperties
          };
        })
      };
      
      // Extract fields from transformed properties (like GeoJSON)
      let categories = {};
      for (const feature of transformedGeoJSON.features || []){
        for (const property_name of Object.keys(feature.properties || {})){
          categories[property_name] = true;
        }
      }
      categories = Array.from(Object.keys(categories));
      setJsonFields(categories);
      
      // Create fieldMatching (like GeoJSON import)
      // IMPORTANT: match by key.name first (what we injected), then by key.title as fallback
      let matching_init = {};
      for (const key of view.spec.description.keys){
        // First try to match by key.name (exact match)
        let foundField = categories.find((json_field_name) => json_field_name === key.name);
        // If not found, try by key.title (like original GeoJSON flow)
        if (!foundField) {
          foundField = categories.find((json_field_name) => key.title.startsWith(json_field_name));
        }
        matching_init[key.name] = {
          "field_title": key.title,
          "matched_json_field": foundField ?? "__default__"
        };
      }
      setFieldMatching(matching_init);
      
      // Update geoJSON with transformed version
      setGeoJSON(transformedGeoJSON);
      setDxfMappingApplied(true);
  }, [isDXFFile, layerMappingValidated, view, dxfRawGeoJSON, dxfMappingApplied, layerMapping]);

  // Make annotations (for both GeoJSON and DXF files)
  useEffect(() => {
    if (geoJSON == null || !view){
      return; // Need both GeoJSON and view to process
    }
    
    // For DXF files, wait for layer mapping to be complete
    if (isDXFFile && !layerMappingValidated) {
      return;
    }
    
    const { annotations, warnings } = buildAnnotationsFromGeojson({
      geojson: geoJSON,
      view,
      fieldMatching,
      isDXFFile,
      layerMapping,
      cleanCorruptedAnnotation,
    });
    setAnnotationsToCreate(annotations);
    setWarnings(warnings);
  }, [fieldMatching, geoJSON, isDXFFile, view])

  // Export GeoJSON to CSV
  const exportToCSV = () => {
    if (!geoJSON || !geoJSON.features) {
      setSnackbar({
        open: true,
        message: "No data to export",
        severity: "warning"
      });
      return;
    }

    // Extract all unique property keys
    const allKeys = new Set();
    geoJSON.features.forEach(feature => {
      Object.keys(feature.properties || {}).forEach(key => allKeys.add(key));
    });
    const headers = ['geometry_type', 'coordinates', ...Array.from(allKeys)];

    // Generate CSV rows
    const rows = geoJSON.features.map(feature => {
      const row = [
        feature.geometry?.type || '',
        JSON.stringify(feature.geometry?.coordinates || []),
        ...Array.from(allKeys).map(key => {
          const value = feature.properties?.[key] ?? '';
          // Escape quotes and newlines for CSV
          return typeof value === 'string' ? value.replace(/"/g, '""').replace(/[\n\r]/g, ' ') : value;
        })
      ];
      return row.map(cell => `"${cell}"`).join(';');
    });

    const csvContent = [
      headers.map(h => `"${h}"`).join(';'),
      ...rows
    ].join('\n');

    // Download CSV
    const blob = new Blob(
      ["\uFEFF" + csvContent], // BOM for UTF-8
      { type: "text/csv;charset=utf-8;" }
    );
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'dwg_export.csv';
    link.click();
    URL.revokeObjectURL(link.href);

    setSnackbar({
      open: true,
      message: "CSV file downloaded successfully!",
      severity: "success"
    });
  };

  // Save annotationsToCreate to DB (minimal)
  const saveDXFToDB = async () => {
    if (!view || !inspectionId) {
      setSnackbar({ open: true, message: "Please select an inspection/view first", severity: "warning" });
      return;
    }

    const list = (annotationsToCreate || []).filter((a) => a && a.geometry);
    if (list.length === 0) {
      setSnackbar({ open: true, message: "No annotations to import", severity: "warning" });
      return;
    }

    setIsImporting(true);
    setErrors([]);
    setWarnings([]);

    try {
      const { created, failed } = await importAnnotationsToDb({
        annotations: list,
        viewId: view.id,
        inspectionId,
        createAnnotationOnAPI,
      });

      if (failed.length > 0) {
        setErrors(failed.map((f) => `Failed to import annotation #${f.index + 1}: ${f.error}`));
        setWarnings((prev) => [
          ...(prev || []),
          `${failed.length} annotation(s) failed to import. See errors list for details.`,
        ]);
      }

      setSnackbar({
        open: true,
        message: `Successfully imported ${created.length} annotation(s) to database${failed.length ? ` (${failed.length} failed)` : ""}.`,
        severity: failed.length ? "warning" : "success",
      });

      if (inspectionId && view?.id) {
        setTimeout(() => {
          navigate(`/ortho?inspectionId=${inspectionId}&viewId=${view.id}`);
        }, 800);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error saving to database: ${error?.message || error}`,
        severity: "error",
      });
    } finally {
      setIsImporting(false);
    }
  };

  // Import annotation
  const importAnnotations = async () => {
    setIsImporting(true);

    let annotationsToCreateCopy = [...annotationsToCreate];
    let failed = 0;
    // Errors stay inside the task so one failure does not stop the rest.
    await runInParallel(annotationsToCreateCopy, 20, async (annotation) => {
      const io = {
        ...annotation,
        viewId: view.id,
        inspectionId: inspectionId
      };
      try {
        const result = await createAnnotationOnAPI(io);
        annotation.id = result.id;
        // Use functional form to avoid stale state
        setAnnotationsToCreate((prev) =>
          prev.map((ann) =>
            ann === annotation ? { ...ann, id: result.id } : ann
          )
        );
      } catch (e) {
        failed += 1;
        setErrors((prev) => [
          ...prev,
          `Failed to import annotation (geometry: ${annotation.geometry?.type || "?"}). ${e?.message || e}`,
        ]);
      }
    });
    if (failed > 0) {
      setWarnings((prev) => [
        ...prev,
        `${failed} annotation(s) failed to import. See errors list for details.`,
      ]);
    }
    setIsImporting(false);
  }

  // List (inspectionId, viewId) pairs
  let inspection_view_pairs = [];
  for (const mission of project.missions){
    for (const inspection of mission.inspections){
      for (const view of inspection.views){
        inspection_view_pairs.push({
          display_name: `Mission "${mission.name}", inspection "${inspection.name}"`,
          inspectionId: inspection.id,
          viewId: view.id
        })
      }
    }
  }


  
  return (
    <Box sx={{ maxWidth: 1200, margin: '2rem auto', padding: '1rem', color: 'white' }}>
      <>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Import annotations</Typography>

          <Paper
            variant="outlined"
            sx={{
              p: 1,
              mb: 2,
              bgcolor: "rgba(0,0,0,0.35)",
              borderColor: "rgba(255,255,255,0.18)",
              color: "white",
            }}
          >
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, mb: 0.5 }}>
              Specifications
            </Typography>
            <List
              dense
              sx={{
                pt: 0,
                pb: 0,
                pl: 2.5,
                listStyleType: "disc",
                "& .MuiListItem-root": { display: "list-item" },
              }}
            >
              <ListItem sx={{ display: "list-item", py: 0.25 }}>
                <ListItemText
                  primary="Accepted formats - GeoJSON (.json/.geojson) or CSV (.csv)."
                  primaryTypographyProps={{ sx: { fontSize: 13, lineHeight: 1.2 } }}
                />
              </ListItem>
              <ListItem sx={{ display: "list-item", py: 0.25 }}>
                <ListItemText
                  primary="Required data - Each annotation must have a geometry + an identifier (e.g. 'id' or 'name')."
                  primaryTypographyProps={{ sx: { fontSize: 13, lineHeight: 1.2 } }}
                />
              </ListItem>
              <ListItem sx={{ display: "list-item", py: 0.25 }}>
                <ListItemText
                  primary="CSV - Use ';' as column separator (not ','). Use '.' for decimals (e.g. 12.34, not 12,34)."
                  primaryTypographyProps={{ sx: { fontSize: 13, lineHeight: 1.2 } }}
                />
              </ListItem>
              <ListItem sx={{ display: "list-item", py: 0.25 }}>
                <ListItemText
                  primary="Field mapping - Match fields after parsing; missing fields can use defaults."
                  primaryTypographyProps={{ sx: { fontSize: 13, lineHeight: 1.2 } }}
                />
              </ListItem>
            </List>
          </Paper>
          {/* NOTE: avoid wrapping the whole page in a single FormControl (MUI warns about multiple InputBase inside) */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom color="white" mb={1}>1. Select the inspection where to import the annotations</Typography>
            <FormControl fullWidth size="small">
            <Select
              name="viewId"
                value={view?.id || ""}
              disabled={isImporting}
              onChange={(e) => handleInspectionChange(e)}
            >
              {inspection_view_pairs.map((el) => {
                return <MenuItem key={el.inspectionId + el.viewId} value={el.viewId}>{el.display_name}</MenuItem>
              })}
              </Select>
            </FormControl>
            <br/>

            {view != null && <>
              <Box
                sx={{
                  mt: 2,
                  mb: 2,
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: "rgba(0,0,0,0.18)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <Typography variant="h6" gutterBottom color="white" mb={1}>
                  2. Select the file of annotations to import
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <ImportConverter
                    disabled={isImporting}
                    onClear={clearImportedGeojson}
                    onErrors={(errs) => setErrors(errs || [])}
                    onConverted={(out) => {
                      if (!out) return;
                      applyImportedGeojson(out);
                    }}
                  />
                </Box>
              </Box>

              {errors.length > 0 && (
                <Box sx={{ mt: 1, mb: 2 }}>
                  <Typography variant="h6" gutterBottom color="white" mb={1}>
                    Errors
                  </Typography>
                  <List dense sx={{ "& .MuiListItem-root": { py: 0.25 } }}>
                    {errors.map((text, idx) => {
                      return (
                        <ListItem key={idx}>
                          <ListItemText primary={text} />
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}

              {/* Field matching for GeoJSON files */}
              {geoJSON != null && !isDXFFile && <><Typography variant="h6" gutterBottom color="white" mb={1}>3. Match the fields with the available field names in the JSON</Typography>
              {Object.entries(fieldMatching).map(([keyName, matching]) => {
                return <>
                  <FormControl
                    variant="outlined"
                    sx={{ width: 200, display: 'inline-flex', ml: 2 }}
                  >
                    <Typography variant="body1">{matching.field_title}:</Typography>
                    <Select
                      key={keyName}
                      value={matching.matched_json_field}
                      style={(matching.matched_json_field === "__default__") ? {"color": "red"} : {}}
                      sx={{ width: '200px' }}
                      disabled={isImporting}
                      onChange={(e) => handleMatchingChange(e, keyName)}
                    >
                      <MenuItem key={"__default__"} value={"__default__"} style={{"color": "red"}}>Default value</MenuItem>
                      {jsonFields.map((json_field_name) => {
                        return <MenuItem key={json_field_name} value={json_field_name}>{json_field_name}</MenuItem>
                      })}
                    </Select>
                  </FormControl>
                </>
              })}<br/><br/></>}

              {/* Layer mapping for DXF files - show BEFORE checking */}
              {isDXFFile && geoJSON != null && uniqueLayers.length > 0 && !layerMappingValidated && (
                <>
                  <Typography variant="h6" gutterBottom color="white" mb={1}>
                    3. Map DXF layers to view fields
                  </Typography>
                  <Typography variant="body2" color="gray" mb={2}>
                    For each unique layer name, select the corresponding field and option value.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={downloadCurrentGeoJSON}
                      disabled={!geoJSON}
                    >
                      Download GeoJSON
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={validateLayerMapping}
                      disabled={!layerMappingReady || isImporting}
                    >
                      Validate mapping
                    </Button>
                    {!layerMappingReady && (
                      <Typography variant="body2" color="orange" sx={{ alignSelf: "center" }}>
                        Please map all layers to enable validation
                      </Typography>
                    )}
                  </Box>
                  <TableContainer component={Paper} sx={{ mb: 2, overflowX: 'hidden' }}>
                    <Table sx={{ width: '100%', tableLayout: 'fixed' }} size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: commentField ? '12%' : '18%' }}>Layer Name</TableCell>
                          <TableCell sx={{ width: '10%' }}>Ignore</TableCell>
                          <TableCell sx={{ width: commentField ? '14%' : '20%' }}>Field (Key)</TableCell>
                          <TableCell sx={{ width: commentField ? '14%' : '20%' }}>Option</TableCell>
                          <TableCell sx={{ width: commentField ? '14%' : '20%' }}>Parent Field (if submenu)</TableCell>
                          <TableCell sx={{ width: commentField ? '14%' : '22%' }}>Parent Option</TableCell>
                          {commentField && <TableCell sx={{ width: '32%' }}>{commentField.title || commentField.name}</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {uniqueLayers.map((layerName) => {
                          const mapping = layerMapping[layerName] || { keyId: '', optionId: '', comment: '', ignored: false };
                          const selectedKey = findKeyByIdOrName(mapping.keyId, view.spec.description.keys);
                          const parentKeys = selectedKey?.id ? getParentKeysForSubmenu(selectedKey.id, view.spec.description.keys) : [];
                          const selectedParentKey = mapping.parentKeyId
                            ? findKeyByIdOrName(mapping.parentKeyId, view.spec.description.keys)
                            : (parentKeys[0] || null);
                          const parentOptions = selectedKey && selectedParentKey && selectedParentKey.type === "select" && selectedParentKey.options
                            ? selectedParentKey.options.filter(opt => (opt.submenus || []).includes(selectedKey.id))
                            : [];
                          
                          return (
                            <TableRow key={layerName}>
                              <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={layerName}>{layerName}</TableCell>
                              <TableCell>
                                <Select
                                  value={mapping.ignored ? "yes" : "no"}
                                  onChange={(e) => handleLayerIgnoredChange(layerName, e.target.value === "yes")}
                                  sx={{ width: '100%', maxWidth: '100%' }}
                                  size="small"
                                  disabled={isImporting}
                                >
                                  <MenuItem value="no">No</MenuItem>
                                  <MenuItem value="yes">Yes</MenuItem>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={mapping.keyId || ''}
                                  onChange={(e) => handleLayerKeyChange(layerName, e.target.value)}
                                  sx={{ width: '100%', maxWidth: '100%' }}
                                  size="small"
                                  disabled={isImporting || mapping.ignored}
                                >
                                  <MenuItem value="">-- Select field --</MenuItem>
                                  {view?.spec.description.keys
                                    .filter(key => key.type === "select" && key.options && key.options.length > 0)
                                    .map((key, idx) => (
                                      <MenuItem key={`${key.id || key.name}-${idx}`} value={key.id || key.name}>
                                        {key.id || key.name}
                                      </MenuItem>
                                    ))}
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={mapping.optionId || ''}
                                  onChange={(e) => handleLayerOptionChange(layerName, e.target.value)}
                                  sx={{ width: '100%', maxWidth: '100%' }}
                                  size="small"
                                  disabled={isImporting || mapping.ignored || !mapping.keyId}
                                >
                                  <MenuItem value="">-- Select option --</MenuItem>
                                  {selectedKey?.options?.map((opt, idx) => (
                                    <MenuItem key={getOptionValue(opt) ? `opt-${getOptionValue(opt)}` : `opt-idx-${idx}`} value={getOptionValue(opt)}>{opt.text || opt.id}</MenuItem>
                                  ))}
                                </Select>
                              </TableCell>
                              <TableCell>
                                {parentKeys.length > 0 ? (
                                  <Select
                                    value={mapping.parentKeyId || (parentKeys[0]?.id || '')}
                                    onChange={(e) => handleLayerParentKeyChange(layerName, e.target.value)}
                                    sx={{ width: '100%', maxWidth: '100%' }}
                                    size="small"
                                    disabled={isImporting || mapping.ignored || !mapping.keyId}
                                  >
                                    {parentKeys.map((pk, idx) => (
                                      <MenuItem key={`${pk.id || pk.name}-${idx}`} value={pk.id || pk.name}>
                                        {pk.id || pk.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                ) : (
                                  <Typography variant="body2" color="gray">--</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {parentKeys.length > 0 && parentOptions.length > 0 ? (
                                  <Select
                                    value={mapping.parentOptionId || ''}
                                    onChange={(e) => handleLayerParentOptionChange(layerName, e.target.value)}
                                    sx={{ width: '100%', maxWidth: '100%' }}
                                    size="small"
                                    disabled={isImporting || mapping.ignored || !mapping.keyId}
                                  >
                                    <MenuItem value="">-- Select parent option --</MenuItem>
                                    {parentOptions.map((opt, idx) => (
                                      <MenuItem key={getOptionValue(opt) ? `parent-${getOptionValue(opt)}` : `parent-idx-${idx}`} value={getOptionValue(opt)}>{opt.text || opt.id}</MenuItem>
                                    ))}
                                  </Select>
                                ) : (
                                  <Typography variant="body2" color="gray">--</Typography>
                                )}
                              </TableCell>
                              {commentField && (
                                <TableCell>
                                  <TextField
                                    value={mapping.comment || ''}
                                    onChange={(e) => handleLayerCommentChange(layerName, e.target.value)}
                                    placeholder={`Enter ${commentField.title || commentField.name}...`}
                                    multiline
                                    rows={2}
                                    size="small"
                                    sx={{ width: '100%', maxWidth: '100%', '& .MuiInputBase-root': { maxWidth: '100%' } }}
                                    disabled={isImporting || mapping.ignored}
                                  />
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* DXF file actions - only show after layer mapping is validated */}
              {isDXFFile && geoJSON != null && view != null && layerMappingValidated && (
                <>
                  <Typography variant="h6" gutterBottom color="white" mb={1}>
                    4. DXF converted: {geoJSON.features?.length || 0} geometries found
                  </Typography>
                  
                  {/* Show warnings like for GeoJSON */}
                  {warnings.length > 0 && (
                    <>
                      <Typography variant="h6" gutterBottom color="white" mb={1}>4. Check warnings</Typography>
                      {annotationsToCreate.length} annotations have been detected. Warnings:
                      <List>
                        {warnings.map((text, idx) => {
                          return <ListItem key={idx}>
                            <ListItemText primary={text} />
                          </ListItem>
                        })}
                      </List>
                    </>
                  )}
                  {warnings.length === 0 && annotationsToCreate.length > 0 && (
                    <>
                      <Typography variant="h6" gutterBottom color="white" mb={1}>5. Check warnings</Typography>
                      {annotationsToCreate.length} annotations have been detected. No warnings
                    </>
                  )}
                  
                  {/* Show DXF parsing diagnostics */}
                  {isDXFFile && annotationsToCreate.length > 0 && annotationsToCreate.some(ann => ann.parsingIssues || ann.diagnosticInfo) && (
                    <>
                      <Typography variant="h6" gutterBottom color="white" mt={2} mb={1}>
                        6. DXF Parsing Diagnostics
                      </Typography>
                      <Typography variant="body2" color="white" mb={2}>
                        {annotationsToCreate.filter(ann => ann.parsingIssues || ann.diagnosticInfo).length} annotation(s) with parsing issues detected
                      </Typography>
                      <TableContainer component={Paper} sx={{ mt: 2, mb: 2 }}>
                        <Table sx={{ minWidth: 650 }} aria-label="dxf parsing diagnostics">
                          <TableHead>
                            <TableRow>
                              <TableCell>Feature Index</TableCell>
                              <TableCell>Layer</TableCell>
                              <TableCell>Geometry Type</TableCell>
                              <TableCell>Parsing Issues</TableCell>
                              <TableCell>Diagnostic Info</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {annotationsToCreate
                              .map((ann, ann_idx) => ({ ann, ann_idx }))
                              .filter(({ ann }) => ann.parsingIssues || ann.diagnosticInfo)
                              .map(({ ann, ann_idx }) => {
                                // Normalize accented characters to avoid encoding issues
                                const normalizeAccents = (str) => {
                                  if (!str) return str;
                                  // First try to fix UTF-8 encoding issues, then normalize accents
                                  try {
                                    // If string contains malformed UTF-8 sequences, try to decode them
                                    const decoded = decodeURIComponent(escape(str));
                                    return decoded.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                                  } catch (e) {
                                    // If decoding fails, just normalize accents
                                    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                                  }
                                };
                                
                                const layer = normalizeAccents(ann.original_properties?.layer || ann.diagnosticInfo?.layer || 'unknown');
                                const geomType = ann.geometryName || ann.geometry?.type || 'unknown';
                                const issues = ann.parsingIssues || 'No issues';
                                const diagInfo = ann.diagnosticInfo ? (
                                  <div style={{ fontSize: '0.85em' }}>
                                    {ann.diagnosticInfo.entityType && <div><strong>Type:</strong> {normalizeAccents(ann.diagnosticInfo.entityType)}</div>}
                                    {ann.diagnosticInfo.degree !== null && <div><strong>Degree:</strong> {ann.diagnosticInfo.degree}</div>}
                                    {ann.diagnosticInfo.numControlPoints !== null && <div><strong>Expected Control Points:</strong> {ann.diagnosticInfo.numControlPoints}</div>}
                                    {ann.diagnosticInfo.controlPointsFound !== undefined && <div><strong>Found Control Points:</strong> {ann.diagnosticInfo.controlPointsFound}</div>}
                                    {ann.diagnosticInfo.numFitPoints !== null && <div><strong>Expected Fit Points:</strong> {ann.diagnosticInfo.numFitPoints}</div>}
                                    {ann.diagnosticInfo.fitPointsFound !== undefined && <div><strong>Found Fit Points:</strong> {ann.diagnosticInfo.fitPointsFound}</div>}
                                    {ann.diagnosticInfo.coordinatesUsed !== undefined && <div><strong>Coordinates Used:</strong> {ann.diagnosticInfo.coordinatesUsed}</div>}
                                    {ann.diagnosticInfo.coordinatesSource && <div><strong>Source:</strong> {ann.diagnosticInfo.coordinatesSource}</div>}
                                  </div>
                                ) : 'No diagnostic info';
                                
                                return (
                                  <TableRow key={`dxf-diagnostic-${ann_idx}`}>
                                    <TableCell>{ann_idx}</TableCell>
                                    <TableCell>{layer}</TableCell>
                                    <TableCell>{geomType}</TableCell>
                                    <TableCell style={{ color: issues !== 'No issues' ? 'orange' : 'inherit' }}>
                                      {issues}
                                    </TableCell>
                                    <TableCell>{diagInfo}</TableCell>
                                  </TableRow>
                                );
                              })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                  
                  {/* Show table with problematic annotations like for GeoJSON */}
                  {annotationsToCreate.length > 0 && annotationsToCreate.some(ann => ann.corrupted_field_names.length > 0) && (
                    <TableContainer component={Paper} sx={{ mt: 2, mb: 2 }}>
                      <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                          <TableRow>
                            <TableCell>Geometry Type</TableCell>
                            {(() => {
                              // Group by name to avoid duplicates, but keep submenus (they are still fields)
                              // Use a Map to track the first occurrence of each name
                              const nameToKeyMap = new Map();
                              view.spec.description.keys.forEach((key) => {
                                if (!nameToKeyMap.has(key.name)) {
                                  nameToKeyMap.set(key.name, key);
                                }
                              });
                              // Return keys in the order they appear, but deduplicated by name
                              const seenNames = new Set();
                              return view.spec.description.keys
                                .filter(key => {
                                  // Only show first occurrence of each name
                                  if (seenNames.has(key.name)) {
                                    return false;
                                  }
                                  seenNames.add(key.name);
                                  return true;
                                })
                                .map((key, keyIdx) => {
                                  const isSubmenu = findParentKey(key, view.spec.description.keys) != null;
                                  return (
                                    <TableCell 
                                      key={`header-${keyIdx}-${key.name}-${key.id || ''}`}
                                      title={isSubmenu ? `Submenu de: ${findParentKey(key, view.spec.description.keys)?.title || 'N/A'}` : undefined}
                                    >
                                      {key.title}
                                      {isSubmenu && ' (submenu)'}
                                    </TableCell>
                                  );
                                });
                            })()}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {annotationsToCreate.filter((ann) => ann.corrupted_field_names.length > 0).map((ann, ann_idx) => {
                            // Get the same filtered keys for the body (deduplicated by name, but keep submenus)
                            const seenNames = new Set();
                            const displayKeys = view.spec.description.keys
                              .filter(key => {
                                // Only show first occurrence of each name
                                if (seenNames.has(key.name)) {
                                  return false;
                                }
                                seenNames.add(key.name);
                                return true;
                              });
                            
                            return <TableRow key={`row-${ann_idx}`}>
                              <TableCell>{ann.geometryName || ann.geometry?.type}</TableCell>
                              {displayKeys.map((key, keyIdx) => {
                                // Check if this field is corrupted, but also check if it's a layer-mapped field
                                // Layer-mapped fields should not be marked as corrupted if they have a valid value
                                const isCorrupted = ann.corrupted_field_names.includes(key.name);
                                const isLayerMapped = ann.layerMappedFields?.includes(key.name);
                                const parentKey = findParentKey(key, view.spec.description.keys);
                                const isSubmenuWithUnsetParent = parentKey && !ann.data?.[parentKey.name] && isLayerMapped;
                                
                                // Priority: data (mapped values) > original_properties (raw feature properties)
                                const value = ann.data?.[key.name] ?? ann.original_properties?.[key.name] ?? null;
                                const isMissing = value === null || value === undefined;
                                
                                // If it's a layer-mapped field with a valid value, don't mark as corrupted
                                // (even if cleanCorruptedAnnotation marked it as corrupted, it's actually valid)
                                const effectiveIsCorrupted = isCorrupted && !(isLayerMapped && value != null);
                                
                                // Normalize accented characters to avoid encoding issues
                                const normalizeAccents = (str) => {
                                  if (!str) return str;
                                  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                                };
                                
                                // For select fields, find the option text to display instead of ID
                                let displayValue = value;
                                if (key.type === "select" && key.options && value != null) {
                                  const option = key.options.find(opt => opt.id === value);
                                  if (option) {
                                    displayValue = option.text || option.id;
                                  }
                                }
                                
                                // Normalize accents in display value
                                if (typeof displayValue === 'string') {
                                  displayValue = normalizeAccents(displayValue);
                                }
                                
                                return <TableCell 
                                  key={`cell-${ann_idx}-${keyIdx}-${key.name}-${key.id || ''}`}
                                  style={
                                    isMissing ? {"background-color": "orange"} :
                                    effectiveIsCorrupted ? {"background-color": "red", "text-decoration": "line-through"} :
                                    isLayerMapped ? {"background-color": "#e3f2fd", "border-left": "3px solid #2196f3"} :
                                    {}
                                  }
                                  title={isLayerMapped ? `Mappé depuis le layer: ${normalizeAccents(ann.original_properties?.layer || 'N/A')}${isSubmenuWithUnsetParent ? ` (Parent "${normalizeAccents(parentKey?.title || 'N/A')}" non défini)` : ''}` : undefined}
                                >
                                  {isLayerMapped && "🔗 "}
                                  {displayValue ?? "∅"}
                                </TableCell>;
                              })}
                            </TableRow>;
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  
                  {isImporting && (
                    <Typography variant="body2" color="white">
                      Importing to database...
                    </Typography>
                  )}
                </>
              )}

              {/* GeoJSON file processing OR DXF after mapping validated (same checking flow) */}
              {geoJSON != null && (!isDXFFile || layerMappingValidated) && <>
                {warnings.length > 0 && <>
                  <Typography variant="h6" gutterBottom color="white" mb={1}>4. Check warnings</Typography>
                  {annotationsToCreate.length} annotations have been detected. Warnings:
                  <List>
                    {warnings.map((text) => {
                      return <ListItem>
                        <ListItemText primary={text} />
                      </ListItem>
                    })}
                  </List>
                </>}
                {warnings.length === 0 && annotationsToCreate.length > 0 && <>
                  <Typography variant="h6" gutterBottom color="white" mb={1}>4. Check warnings</Typography>
                  {annotationsToCreate.length} annotations have been detected. No warnings
                </>}

                <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        {jsonFields.map((col_name) => {
                          for (const [keyName, matching] of Object.entries(fieldMatching)){
                            if (matching.matched_json_field === col_name){
                              return <TableCell>{col_name}</TableCell>;
                            }
                          }
                          return <TableCell style={{"background-color": "grey", "text-decoration": "line-through"}}>{col_name}</TableCell>;
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {annotationsToCreate.filter((ann) => ann.corrupted_field_names.length > 0).map((ann, ann_idx) => {
                        // Normalize accented characters to avoid encoding issues
                        const normalizeAccents = (str) => {
                          if (!str) return str;
                          // First try to fix UTF-8 encoding issues, then normalize accents
                          try {
                            // If string contains malformed UTF-8 sequences, try to decode them
                            const decoded = decodeURIComponent(escape(str));
                            return decoded.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                          } catch (e) {
                            // If decoding fails, just normalize accents
                            return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                          }
                        };
                        
                        return <TableRow key={ann_idx}>
                          {jsonFields.map((col_name) => {
                            for (const [keyName, matching] of Object.entries(fieldMatching)){
                              if (matching.matched_json_field === col_name){
                                // Use annotation.data (processed values) not original_properties
                                const value = ann.data?.[keyName] ?? ann.original_properties?.[col_name] ?? null;
                                const isMissing = value === null || value === undefined;
                                const isCorrupted = ann.corrupted_field_names.indexOf(keyName) >= 0;
                                
                                // For select fields, find the option text to display instead of ID
                                let displayValue = value;
                                const key = view.spec.description.keys.find(k => k.name === keyName);
                                if (key && key.type === "select" && key.options && value != null) {
                                  const option = key.options.find(opt => opt.id === value || opt.text === value);
                                  if (option) {
                                    displayValue = option.text || option.id;
                                  }
                                }
                                
                                // Case 1: OK
                                if (!isCorrupted && !isMissing){
                                  return <TableCell>{normalizeAccents(String(displayValue ?? "∅"))}</TableCell>;
                                }
                                // Case 2: Missing
                                else if (isMissing){
                                  return <TableCell style={{"background-color": "orange"}}>∅</TableCell>;
                                }
                                // Case 3: wrong/corrupted
                                else {
                                  return <TableCell style={{"background-color": "red", "text-decoration": "line-through"}}>
                                    {normalizeAccents(String(displayValue ?? "∅"))}
                                  </TableCell>;
                                }
                              }
                            }
                            const value = ann.original_properties?.[col_name];
                            return <TableCell style={{"background-color": "grey", "text-decoration": "line-through"}}>{value != null ? normalizeAccents(String(value)) : "null"}</TableCell>;
                          })}
                        </TableRow>
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <br/><br/>
              </>}


              {/* Import button (for both GeoJSON and DXF files) */}
              {geoJSON != null && (!isDXFFile || layerMappingValidated) && <>
                <Typography variant="h6" gutterBottom color="white" mb={1}>
                  5. Import the annotations
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 2 }}>
                  {isDXFFile && (
                    <Button 
                      variant="contained" 
                      color="secondary" 
                      onClick={exportToCSV}
                      disabled={isImporting || geoJSON == null}
                    >
                      To CSV
                    </Button>
                  )}
                  {isImporting === false && (
                    <Button 
                      variant="contained" 
                      onClick={importAnnotations}
                      disabled={isDXFFile && !layerMappingValidated}
                    >
                  Start importing!
                    </Button>
                  )}
                  {isImporting === true && (
                    <Typography variant="body2" color="white">
                  {annotationsToCreate.filter((ann) => ann.id != null).length}/{annotationsToCreate.length} annotations imported!
                    </Typography>
                  )}
                </Box>
                <br/><br/>
              </>}
                
            </>}
          </Box>
        </Paper>
      </>
    </Box>
  );
}

export default ProjectImportHandler;